/**
 * Deterministic search over a hex tile map: `findPath` (A*), `reachableFrom`
 * (breadth-first, on the map's numbered grid from `gridOf`), and the
 * "nearest reachable tile" fallbacks move orders use. Every tie-break is
 * fixed, so the sim's paths match on every peer.
 */
import { axialHexDistance } from './coords';
import { getHexTileMapKey } from './model/keys';
import { AXIAL_NEIGHBOR_OFFSETS, getTile, isPassable } from './model/map';
import type { HexTileCoordinates, HexTileMap, HexTileMapKey, HexTileType } from './model/types';

type OpenNode = {
  coordinates: HexTileCoordinates;
  key: HexTileMapKey;
  g: number;
  f: number;
};

/** Whether open node `a` is taken before `b`: min f, then min g, then ascending key. */
function before(a: OpenNode, b: OpenNode): boolean {
  if (a.f !== b.f) return a.f < b.f;
  if (a.g !== b.g) return a.g < b.g;
  return a.key < b.key;
}

/** A* open set: a binary min-heap in `before` order. */
class OpenHeap {
  private readonly nodes: OpenNode[] = [];

  push(node: OpenNode): void {
    const nodes = this.nodes;
    nodes.push(node);
    let i = nodes.length - 1;
    while (i > 0) {
      const up = (i - 1) >> 1;
      if (!before(nodes[i], nodes[up])) break;
      [nodes[i], nodes[up]] = [nodes[up], nodes[i]];
      i = up;
    }
  }

  pop(): OpenNode | undefined {
    const nodes = this.nodes;
    const top = nodes[0];
    const last = nodes.pop();
    if (nodes.length === 0 || last === undefined) return top;
    nodes[0] = last;
    let i = 0;
    for (;;) {
      const l = 2 * i + 1;
      const r = l + 1;
      let best = i;
      if (l < nodes.length && before(nodes[l], nodes[best])) best = l;
      if (r < nodes.length && before(nodes[r], nodes[best])) best = r;
      if (best === i) break;
      [nodes[i], nodes[best]] = [nodes[best], nodes[i]];
      i = best;
    }
    return top;
  }
}

/**
 * A* pathfinding over a {@link HexTileMap} with uniform step cost 1.
 *
 * Returns `[start, ..., goal]` inclusive of both endpoints.
 * Returns `[start]` when start equals goal (and start is passable).
 * Returns `null` when start or goal is missing/impassable, or the goal is unreachable.
 * `blocked` tiles (e.g. enemy units and rival buildings) are impassable too,
 * except `start`.
 *
 * Deterministic: identical inputs always produce identical arrays regardless of
 * Map insertion order. Tie-breaking: min f, then min g, then ascending key string.
 */
function findPath(
  tiles: HexTileMap,
  start: HexTileCoordinates,
  goal: HexTileCoordinates,
  terrainList: readonly HexTileType[],
  blocked?: ReadonlySet<HexTileMapKey>
): HexTileCoordinates[] | null {
  if (!isPassable(getTile(tiles, start), terrainList)) return null;
  if (!isPassable(getTile(tiles, goal), terrainList)) return null;

  const startKey = getHexTileMapKey(start);
  const goalKey = getHexTileMapKey(goal);

  if (startKey === goalKey) return [start];
  if (blocked?.has(goalKey)) return null;

  const gScore = new Map<HexTileMapKey, number>();
  const cameFrom = new Map<HexTileMapKey, HexTileCoordinates>();

  gScore.set(startKey, 0);

  // A binary heap ordered by min f, then min g, then ascending key: the same
  // node a linear scan of the open set would pick. A better route to an open
  // node pushes a fresh record; the stale one pops later and is skipped.
  const open = new OpenHeap();
  const closed = new Set<HexTileMapKey>();

  open.push({ coordinates: start, key: startKey, g: 0, f: axialHexDistance(start, goal) });

  for (let current = open.pop(); current; current = open.pop()) {
    if (closed.has(current.key) || current.g !== gScore.get(current.key)) continue;
    closed.add(current.key);

    if (current.key === goalKey) {
      // Reconstruct path.
      const path: HexTileCoordinates[] = [current.coordinates];
      let cursor = current.key;
      while (cameFrom.has(cursor)) {
        const prev = cameFrom.get(cursor) as HexTileCoordinates;
        path.push(prev);
        cursor = getHexTileMapKey(prev);
      }
      path.reverse();
      return path;
    }

    for (const offset of AXIAL_NEIGHBOR_OFFSETS) {
      const neighbor = { q: current.coordinates.q + offset.q, r: current.coordinates.r + offset.r };
      const neighborKey = getHexTileMapKey(neighbor);
      if (closed.has(neighborKey) || blocked?.has(neighborKey)) continue;
      if (!isPassable(tiles.get(neighborKey), terrainList)) continue;

      const tentativeG = current.g + 1;
      const existingG = gScore.get(neighborKey) ?? Number.POSITIVE_INFINITY;

      if (tentativeG < existingG) {
        cameFrom.set(neighborKey, current.coordinates);
        gScore.set(neighborKey, tentativeG);
        const f = tentativeG + axialHexDistance(neighbor, goal);
        open.push({ coordinates: neighbor, key: neighborKey, g: tentativeG, f });
      }
    }
  }

  return null;
}

/** The read side of a map from tile keys: all a {@link Reachable} offers. */
interface KeyLookup<V> {
  get(key: HexTileMapKey): V | undefined;
  has(key: HexTileMapKey): boolean;
}

interface Reachable {
  /** Reachable tiles in breadth-first order (start first). */
  order: HexTileCoordinates[];
  /** Steps from start to each reachable tile. */
  dist: KeyLookup<number>;
  /** Previous tile on a shortest path from start (null for start). */
  parent: KeyLookup<HexTileCoordinates | null>;
  /** Each tile of `order`'s key, when the search recorded them (`reachableFrom` does). */
  keys?: HexTileMapKey[];
}

/**
 * A map's tiles numbered, with each tile's neighbours by number (-1 off the
 * map) in canonical order: searches then work on arrays instead of building
 * keys and filling maps. It depends only on which tiles exist, so it's
 * memoised per map and rebuilt when the map's size changes (a tile's type
 * may change in place: searches still read types from the map).
 */
interface TileGrid {
  size: number;
  keys: HexTileMapKey[];
  index: Map<HexTileMapKey, number>;
  /** `neighbors[i * 6 + k]`: tile i's k-th neighbour (AXIAL_NEIGHBOR_OFFSETS order), or -1. */
  neighbors: Int32Array;
  /** The tiles' bounding box (`width` × `height` from `minQ`, `minR`)… */
  minQ: number;
  minR: number;
  width: number;
  height: number;
  /** …and each box cell's tile index, -1 for none: see `tileAt`. */
  at: Int32Array;
}

/** Index in `grid` of the tile at q, r, or -1 when there's none. */
function tileAt(grid: TileGrid, q: number, r: number): number {
  const x = q - grid.minQ;
  const y = r - grid.minR;
  if (x < 0 || y < 0 || x >= grid.width || y >= grid.height) return -1;
  return grid.at[x * grid.height + y];
}

const grids = new WeakMap<HexTileMap, TileGrid>();

function gridOf(tiles: HexTileMap): TileGrid {
  const cached = grids.get(tiles);
  if (cached && cached.size === tiles.size) return cached;
  const keys = [...tiles.keys()];
  const index = new Map<HexTileMapKey, number>();
  keys.forEach((key, i) => {
    index.set(key, i);
  });
  const neighbors = new Int32Array(keys.length * 6).fill(-1);
  keys.forEach((key, i) => {
    const tile = tiles.get(key);
    if (!tile) return;
    const { q, r } = tile.coordinates;
    AXIAL_NEIGHBOR_OFFSETS.forEach((offset, k) => {
      neighbors[i * 6 + k] = index.get(`${q + offset.q},${r + offset.r}`) ?? -1;
    });
  });
  let minQ = 0;
  let minR = 0;
  let maxQ = -1;
  let maxR = -1;
  keys.forEach((key, i) => {
    const c = tiles.get(key)?.coordinates;
    if (!c) return;
    if (i === 0 || c.q < minQ) minQ = c.q;
    if (i === 0 || c.r < minR) minR = c.r;
    if (i === 0 || c.q > maxQ) maxQ = c.q;
    if (i === 0 || c.r > maxR) maxR = c.r;
  });
  const width = maxQ - minQ + 1;
  const height = maxR - minR + 1;
  const at = new Int32Array(Math.max(0, width * height)).fill(-1);
  keys.forEach((key, i) => {
    const c = tiles.get(key)?.coordinates;
    if (c) at[(c.q - minQ) * height + (c.r - minR)] = i;
  });
  const grid = { size: tiles.size, keys, index, neighbors, minQ, minR, width, height, at };
  grids.set(tiles, grid);
  return grid;
}

/**
 * Every tile reachable from `start` over passable terrain, with shortest-path
 * distances. Breadth-first in canonical neighbour order, so deterministic.
 * Empty when `start` itself is missing/impassable. `blocked` tiles are never
 * entered. The hot search of every big map: it runs on the map's numbered
 * grid (`gridOf`), with each tile's key built once per map, its terrain
 * checked once per search, and lookups answered from arrays.
 *
 * `stop` ends the search at the first tile (start included) it accepts, in
 * search order: the result then holds only the tiles reached up to it, a
 * prefix of the full search, with the same distances and paths.
 *
 * `within` bounds the search: tiles it rejects are never entered, like
 * `blocked` ones (the start is always searched from), so distances and
 * paths are the shortest that stay inside the bound.
 */
function reachableFrom(
  tiles: HexTileMap,
  start: HexTileCoordinates,
  terrainList: readonly HexTileType[],
  blocked?: ReadonlySet<HexTileMapKey>,
  stop?: (cell: HexTileCoordinates) => boolean,
  within?: (q: number, r: number) => boolean
): Reachable {
  const startKey = getHexTileMapKey(start);
  const grid = gridOf(tiles);
  const startIndex = grid.index.get(startKey);
  if (startIndex === undefined || !isPassable(getTile(tiles, start), terrainList)) {
    return { order: [], dist: new Map(), parent: new Map() };
  }
  const { keys: gridKeys, index, neighbors } = grid;
  const n = gridKeys.length;
  // Steps from start (-1: not reached), the parent's index, and terrain seen
  // impassable (so it's only looked up once).
  const steps = new Int32Array(n).fill(-1);
  const parents = new Int32Array(n).fill(-1);
  const impassable = new Uint8Array(n);
  // Tile index → its cell in `order`.
  const cells: Array<HexTileCoordinates | undefined> = new Array(n);
  const order: HexTileCoordinates[] = [start];
  const queue: number[] = [startIndex];
  const keys = [startKey];
  steps[startIndex] = 0;
  cells[startIndex] = start;
  let done = stop?.(start) ?? false;
  for (let i = 0; i < queue.length && !done; i++) {
    const at = queue[i];
    const g = steps[at] + 1;
    for (let k = 0; k < 6; k++) {
      const nb = neighbors[at * 6 + k];
      if (nb < 0 || steps[nb] >= 0 || impassable[nb]) continue;
      const key = gridKeys[nb];
      if (blocked?.has(key)) continue;
      const tile = tiles.get(key);
      if (
        !tile ||
        !isPassable(tile, terrainList) ||
        (within && !within(tile.coordinates.q, tile.coordinates.r))
      ) {
        impassable[nb] = 1;
        continue;
      }
      steps[nb] = g;
      parents[nb] = at;
      const cell = { q: tile.coordinates.q, r: tile.coordinates.r };
      cells[nb] = cell;
      order.push(cell);
      queue.push(nb);
      keys.push(key);
      if (stop?.(cell)) {
        done = true;
        break;
      }
    }
  }
  const reached = (key: HexTileMapKey): number => {
    const i = index.get(key);
    return i === undefined ? -1 : steps[i];
  };
  return {
    order,
    keys,
    dist: {
      get: (key) => {
        const d = reached(key);
        return d < 0 ? undefined : d;
      },
      has: (key) => reached(key) >= 0,
    },
    parent: {
      get: (key) => {
        const i = index.get(key);
        if (i === undefined || steps[i] < 0) return undefined;
        const p = parents[i];
        return p < 0 ? null : cells[p];
      },
      has: (key) => reached(key) >= 0,
    },
  };
}

/** `[start, ..., cell]` along the shortest path recorded in `reachable`. */
function pathTo(reachable: Reachable, cell: HexTileCoordinates): HexTileCoordinates[] {
  const path: HexTileCoordinates[] = [cell];
  let prev = reachable.parent.get(getHexTileMapKey(cell));
  while (prev) {
    path.push(prev);
    prev = reachable.parent.get(getHexTileMapKey(prev));
  }
  return path.reverse();
}

/**
 * Like {@link findPath}, but when `goal` can't be reached (impassable, e.g.
 * water, blocked, or on a disconnected landmass) it paths to the reachable
 * tile closest to `goal` instead.
 *
 * Returns `null` only when `start` is missing/impassable. When rerouting, the
 * chosen tile minimises hex distance to `goal`, then path length, then key,
 * and the path to it is a shortest one, so the result is deterministic.
 */
function findPathToNearest(
  tiles: HexTileMap,
  start: HexTileCoordinates,
  goal: HexTileCoordinates,
  terrainList: readonly HexTileType[],
  blocked?: ReadonlySet<HexTileMapKey>
): HexTileCoordinates[] | null {
  const direct = findPath(tiles, start, goal, terrainList, blocked);
  if (direct !== null) return direct;
  const reachable = reachableFrom(tiles, start, terrainList, blocked);
  const best = nearestReachable(reachable, goal);
  return best === null ? null : pathTo(reachable, best);
}

/**
 * The tile of `reachable` closest to `goal` (`goal` itself when reached):
 * least hex distance, then path length, then key. Null when nothing is
 * reachable (an impassable start).
 */
function nearestReachable(
  reachable: Reachable,
  goal: HexTileCoordinates
): HexTileCoordinates | null {
  if (reachable.order.length === 0) return null;
  if (reachable.dist.has(getHexTileMapKey(goal))) return goal;
  let best = reachable.order[0];
  let bestKey = getHexTileMapKey(best);
  let bestH = axialHexDistance(best, goal);
  let bestG = 0;
  for (const cell of reachable.order) {
    const key = getHexTileMapKey(cell);
    const h = axialHexDistance(cell, goal);
    const g = reachable.dist.get(key) ?? 0;
    if (h < bestH || (h === bestH && (g < bestG || (g === bestG && key < bestKey)))) {
      best = cell;
      bestKey = key;
      bestH = h;
      bestG = g;
    }
  }
  return best;
}

export {
  findPath,
  findPathToNearest,
  gridOf,
  type KeyLookup,
  nearestReachable,
  pathTo,
  type Reachable,
  reachableFrom,
  type TileGrid,
  tileAt,
};
